/**
 * @file RegisterPersonalPage.tsx
 * @brief `/register`: Create account, the phone's RegisterPersonal +
 *        RegisterPassword + the two post-signup questions, on one route.
 *
 * @details
 * Three sections (About Yourself, Contact Information, Your Preferences),
 * then the password, then "where did you hear about us" and "what will you
 * use it for". Like the phone, the user then signs in; the token from
 * `/register` is used only to file the two answers and is never stored.
 *
 * @references
 * - FRONTEND/src/screens/Auth/RegisterPersonal.tsx, RegisterPassword.tsx
 * - BACKEND/src/features/auth/routes.js (registerSchema)
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, Phone, User } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { toast } from "@/shared/hooks/useToast";
import { checkEmailAvailable, registerUser, acceptCurrentTerms } from "./registerApi";
import {
  describeRegisterError,
  phoneNumberSchema,
  useTakenCheck,
  type RegisteredState,
} from "./registerShared";
import {
  NativeLanguageField,
  PasswordStep,
  RegisterHeader,
  RegisterSection,
  SignInLink,
  SignupQuestions,
  TitleField,
} from "./registerUi";

const schema = z
  .object({
    title: z.string().min(1, "Required"),
    firstName: z.string().trim().min(1, "Required"),
    lastName: z.string().trim().min(1, "Required"),
    email: z.string().trim().email("Enter a valid email"),
    confirmEmail: z.string().trim().email("Enter a valid email"),
    phoneNumber: phoneNumberSchema,
    nativeLanguage: z.string().min(1, "Native language is required"),
  })
  .refine((d) => d.email.toLowerCase() === d.confirmEmail.toLowerCase(), {
    message: "Emails don't match",
    path: ["confirmEmail"],
  });
type FormValues = z.infer<typeof schema>;

const emailCheck = async (email: string) => {
  const available = await checkEmailAvailable(email);
  return available === null ? null : { available };
};

export function RegisterPersonalPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"details" | "password" | "questions">("details");
  const [details, setDetails] = useState<FormValues | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    getFieldState,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      title: "",
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      phoneNumber: "",
      // Empty on purpose (phone, 27-09-2026): a deliberate answer, not a
      // guess from the browser's locale.
      nativeLanguage: "",
    },
  });

  const values = useWatch({ control });
  const email = (values.email ?? "").trim();
  const emailTaken = useTakenCheck(email, z.string().email().safeParse(email).success, emailCheck);

  // A "taken" error belongs to the value it was found for: editing the
  // field drops it, and the live check flags the new value if it is taken too.
  useEffect(() => {
    if (getFieldState("email").error?.type === "taken") clearErrors("email");
  }, [email, getFieldState, clearErrors]);
  useEffect(() => {
    if (emailTaken.takenValue) {
      setError("email", { type: "taken", message: "Email already registered" });
    }
  }, [emailTaken.takenValue, setError]);

  const filled = (keys: (keyof FormValues)[]) =>
    keys.every((k) => String(values[k] ?? "").trim().length > 0 && !errors[k]);

  const onContinue = handleSubmit(async (v) => {
    // Ask once more: a live check that got no answer must not let a
    // registered email through to the password step.
    if (emailTaken.takenValue || (await checkEmailAvailable(v.email.trim())) === false) {
      setError("email", { type: "taken", message: "Email already registered" });
      return;
    }
    setDetails(v);
    setServerError(null);
    setStep("password");
  });

  const goLogin = () => navigate("/login");

  const finish = () => {
    if (!details) return;
    toast({
      title: "Registration Successful!",
      description: "Your account has been created successfully. Please log in to continue.",
    });
    const state: RegisteredState = {
      registeredEmail: details.email.trim(),
      accountType: "personal",
    };
    navigate("/login", { replace: true, state });
  };

  if (step === "questions" && token) {
    return <SignupQuestions token={token} onDone={finish} />;
  }

  if (step === "password" && details) {
    return (
      <PasswordStep
        title="Set password"
        onBack={() => setStep("details")}
        serverError={serverError}
        onCreate={async (password) => {
          setServerError(null);
          try {
            const res = await registerUser({
              title: details.title,
              firstName: details.firstName.trim(),
              lastName: details.lastName.trim(),
              email: details.email.trim(),
              phoneNumber: details.phoneNumber.trim(),
              nativeLanguage: details.nativeLanguage,
              password,
            });
            setToken(res.token);
            void acceptCurrentTerms(res.token);
            setStep("questions");
          } catch (err) {
            setServerError(describeRegisterError(err));
          }
        }}
      />
    );
  }

  return (
    <div>
      <RegisterHeader
        title="Create account"
        subtitle={"Two minutes, and you are ready\nfor your first meeting."}
        onBack={goLogin}
      />

      {/* Two columns from `sm` (DES 01-10-2026): the form fits one screen
          without scrolling, the desktop app's window included. Reading order
          stays the phone's; Continue sits beside the last section. */}
      <form
        onSubmit={onContinue}
        className="grid gap-x-10 gap-y-7 sm:grid-cols-2 [@media(max-height:720px)]:gap-y-4"
        noValidate
      >
        <RegisterSection
          label="About Yourself"
          done={filled(["title", "firstName", "lastName"])}
        >
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <TitleField value={field.value} onChange={field.onChange} error={errors.title?.message} />
            )}
          />
          <FormFieldGroup label="First Name" htmlFor="firstName" icon={<User />} error={errors.firstName?.message}>
            <Input id="firstName" autoComplete="given-name" capitalizeFirst {...register("firstName")} />
          </FormFieldGroup>
          <FormFieldGroup label="Last Name" htmlFor="lastName" icon={<User />} error={errors.lastName?.message}>
            <Input id="lastName" autoComplete="family-name" capitalizeFirst {...register("lastName")} />
          </FormFieldGroup>
        </RegisterSection>

        <RegisterSection
          label="Contact Information"
          done={filled(["email", "confirmEmail", "phoneNumber"])}
        >
          <FormFieldGroup label="Email Address" htmlFor="email" icon={<Mail />} error={errors.email?.message}>
            <Input id="email" type="email" autoComplete="email" {...register("email")} />
          </FormFieldGroup>
          <FormFieldGroup
            label="Confirm Email"
            htmlFor="confirmEmail"
            icon={<Mail />}
            error={errors.confirmEmail?.message}
          >
            <Input
              id="confirmEmail"
              type="email"
              autoComplete="email"
              {...register("confirmEmail")}
            />
          </FormFieldGroup>
          <FormFieldGroup label="Phone Number" htmlFor="phoneNumber" icon={<Phone />} error={errors.phoneNumber?.message}>
            <Input id="phoneNumber" type="tel" autoComplete="tel" {...register("phoneNumber")} />
          </FormFieldGroup>
        </RegisterSection>

        <RegisterSection label="Your Preferences" done={filled(["nativeLanguage"])}>
          <Controller
            control={control}
            name="nativeLanguage"
            render={({ field }) => (
              <NativeLanguageField
                value={field.value}
                onChange={field.onChange}
                error={errors.nativeLanguage?.message}
              />
            )}
          />
        </RegisterSection>

        <div className="sm:self-end">
          <Button
            type="submit"
            className="h-12 w-full bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90"
            disabled={isSubmitting}
            size="lg"
          >
            Continue
          </Button>
          <SignInLink onClick={goLogin} />
        </div>
      </form>
    </div>
  );
}
